import React, { useState } from "react";
import {
  Box,
  Stack,
  Divider,
  IconButton,
  TextField,
  Button,
  Stepper,
  Step,
  StepLabel,
  Typography,
  styled,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../../store";
import { useLoader } from "../../../../context/LoaderContext";
import { useNotification } from "../../../../context/NotificationContext";
import { addCase } from "../../../../features/slices/Case";
import { useDropzone } from "react-dropzone";
import { JsonEditor } from "json-edit-react";
import schema from "../addCaseSchema.json";
import {Case} from "../../../../interfaces/Case";
import Ajv from "ajv";
const ajv = new Ajv();
const validate = ajv.compile(schema);
const FormContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "20px",
  maxWidth: "820px",
  width: "100%",
});


const CaseAddForm = ({
  suiteId,
  onClose,
}: {
  suiteId: number;
  onClose: () => void;
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { showLoader, hideLoader } = useLoader();
  const { notify } = useNotification();

  const [cases, setCases] = useState<Case[]>([]);
  const [currentCaseIndex, setCurrentCaseIndex] = useState(0);

  const handleFinish = async () => {
    if (cases.some((caseItem) => !caseItem.title || !caseItem.manifest)) {
      notify("Please complete all cases before finishing.", "error");
      return;
    }

    showLoader();
    const casePromises = cases.map((caseItem) => {
      if (caseItem.title && caseItem.manifest) {
        return dispatch(addCase({ ...caseItem, suite_id: suiteId })).unwrap();
      }
      return Promise.resolve(null);
    });

    try {
      const results = await Promise.all(casePromises);
      const allCasesAdded = results.every((result) => result !== null);

      if (allCasesAdded) {
        notify("All cases added successfully.", "success");
        onClose();
      } else {
        notify("Some cases were not added. Please check your data.", "error");
      }
    } catch (error) {
      notify("Failed to add all cases. Please try again.", "error");
      console.error("Error adding cases:", error);
    } finally {
      hideLoader();
    }
  };

  const handleApply = async (index: number) => {
    showLoader();
    try {
      const caseObj = {
        ...cases[index],
        suite_id: suiteId,
      };
      const result = await dispatch(addCase(caseObj));
      if (result.type === "case/addCase/fulfilled") {
        hideLoader();
        notify(`Case added successfully for suite ${suiteId}`, "success");
        setCases((prevCases) => [
          ...prevCases.slice(0, index),
          { title: "", manifest: null, meta_data: {}, tool_data: {} },
        ]);
      }
    } catch (e) {
      hideLoader();
      notify("Failed to add case", "error");
      console.error(e);
    }
  };

  const handleAddCase = () => {
    setCases((prevCases) => [
      ...prevCases,
      {
        title: "",
        manifest: null,
        meta_data: {},
        tool_data: {},
        file_name: "",
        original_path: "",
      },
    ]);
  };

  const handleTestCaseChange = (index: number, field: string, value: any) => {
    setCurrentCaseIndex(index);

    const newTestCases = [...cases];
    newTestCases[index][field] = value;
    setCases(newTestCases);
  };

  const handleJsonChange = (index: number, value: any) => {
    const newTestCases = [...cases];
    newTestCases[index].manifest = value.jsObject?.manifest || {};
    newTestCases[index].meta_data = value.jsObject?.meta_data || {};
    newTestCases[index].tool_data = value.jsObject?.tool_data || {};
    newTestCases[index].file_name = value.jsObject?.file_name || "";
    newTestCases[index].original_path = value.jsObject?.original_path || "";
    setCases(newTestCases);
  };

  const onDrop = (acceptedFiles: File[], index: number) => {
    const file: File = acceptedFiles[0];
    const reader = new FileReader();
    reader.onload = (event: ProgressEvent<FileReader>) => {
      const content: string | ArrayBuffer | null = event.target?.result || null;
      try {
        const jsonContent = JSON.parse(content as string);
        const valid = validate(jsonContent);
        if (!valid) {
          notify("Invalid JSON file", "error");
          console.error(validate.errors);
        }
        if (valid) {
          handleJsonChange(index, { jsObject: jsonContent });
        }
      } catch (error) {
        notify("Invalid JSON file", "error");
        console.error("Invalid JSON file");
      }
    };
    reader.readAsText(file);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (acceptedFiles) => {
      onDrop(acceptedFiles, currentCaseIndex);
    },
  });

  return (
    <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
      <FormContainer>
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: 18, lineHeight: 1.2 }}>
            Test cases
          </Typography>
          <Typography color="text.secondary" sx={{ fontSize: 13, mt: 0.5 }}>
            Add one or more cases. Drop a <code>resources_needed.json</code> to
            fill the manifest, give it a title, then apply.
          </Typography>
        </Box>

        {cases.map((testCase, index) => (
          <Box
            key={index}
            sx={{
              p: { xs: 2, sm: 2.5 },
              border: "1px solid var(--app-border, #e4e7ec)",
              borderRadius: "10px",
              backgroundColor: "var(--app-surface, #fff)",
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 1.5 }}
            >
              <Typography sx={{ fontWeight: 800, fontSize: 15 }}>
                Test case {index + 1}
              </Typography>
              <IconButton
                size="small"
                aria-label="Remove case"
                onClick={() =>
                  setCases((prev) => prev.filter((_, i) => i !== index))
                }
                disabled={cases.length <= 1}
                sx={{ color: "#98a2b3", "&:hover": { color: "error.main" } }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Stack>

            <TextField
              label="Title"
              placeholder="e.g. Menu navigation"
              fullWidth
              variant="outlined"
              sx={{ mb: 2 }}
              value={testCase.title}
              onChange={(e) =>
                handleTestCaseChange(index, "title", e.target.value)
              }
            />

            <Box
              {...getRootProps({ onClick: () => setCurrentCaseIndex(index) })}
              sx={{
                border: "1.5px dashed var(--app-border, #cbd5e1)",
                borderRadius: "10px",
                p: 2.5,
                mb: 2,
                textAlign: "center",
                cursor: "pointer",
                backgroundColor: "var(--app-surface-muted, #f8fafc)",
                transition: "border-color 120ms, background-color 120ms",
                "&:hover": {
                  borderColor: "primary.main",
                  backgroundColor: "var(--app-hover, #fff7f1)",
                },
              }}
            >
              <input {...getInputProps()} />
              <CloudUploadIcon sx={{ color: "primary.main", fontSize: 26 }} />
              <Typography sx={{ fontWeight: 700, fontSize: 14, mt: 0.5 }}>
                Drop a resources_needed.json here, or click to select
              </Typography>
              <Typography color="text.secondary" sx={{ fontSize: 12 }}>
                Fills the manifest below; you can still edit it by hand.
              </Typography>
            </Box>

            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: 0.5,
                textTransform: "uppercase",
                color: "text.secondary",
                mb: 0.75,
              }}
            >
              Manifest (JSON)
            </Typography>
            <Box sx={{ mb: 2 }}>
              {testCase.manifest && (
                <JsonEditor
                  data={testCase}
                  collapse={1}
                  onUpdate={(value) => handleJsonChange(index, value)}
                />
              )}
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                onClick={() => handleApply(index)}
                disabled={!testCase.title || !testCase.manifest}
              >
                Save case
              </Button>
            </Box>
          </Box>
        ))}

        <Divider />
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Button variant="outlined" onClick={handleAddCase}>
            {cases.length > 0 ? "Add another case" : "Add case"}
          </Button>
          <Button variant="contained" color="primary" onClick={handleFinish}>
            {cases.length > 1 ? "Apply all" : "Finish"}
          </Button>
        </Box>
      </FormContainer>
    </Box>
  );
};

export default CaseAddForm;
